import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Input, Segmented } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InvoiceClient } from "../api/invoice";
import { InvoicesTable } from "../components/InvoicesTable";
import { NexpimView } from "../views";
import { isoDate } from "../api/reporting";

/**
 * Sales → Invoices: the invoiced-history master grid.
 *
 * The Vaadin view hard-coded its window to last-year-through-today; the same
 * default applies here, but the range is editable — wanting a different year
 * shouldn't mean asking a developer.
 *
 * "Awaiting invoice" switches the same grid to confirmed slips that have no
 * invoice number yet. Invoice generation runs daily, so a slip confirmed
 * before the last run and still unnumbered was skipped by it: those are Stuck,
 * and their count sits beside the toggle while that view is open.
 */
export function InvoicesPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const client = useMemo(() => new InvoiceClient(http), [http]);
  const allowed = hasView(NexpimView.SALES_INVOICES);

  const [view, setView] = useState<"invoiced" | "awaiting">("invoiced");
  const [from, setFrom] = useState(() => `${new Date().getFullYear() - 1}-01-01`);
  const [to, setTo] = useState(() => isoDate(new Date()));

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice", "period", from, to],
    queryFn: () => client.byPeriod({ from, to }),
    enabled: allowed && valid && view === "invoiced",
    staleTime: 60_000,
  });

  const awaiting = useQuery({
    queryKey: ["invoice", "awaiting"],
    queryFn: () => client.awaiting(),
    enabled: allowed && view === "awaiting",
    staleTime: 60_000,
  });
  const stuck = (awaiting.data ?? []).filter((row) => row.pending_state === "STUCK").length;

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.sales-invoices", "Invoices")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.sales-invoices", "Invoices")}
        subtitle={t("invoices.subtitle", "Invoiced sales history.")}
      />

      {(view === "invoiced" ? error : awaiting.error) ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <InvoicesTable
          data={view === "invoiced" ? (data ?? []) : (awaiting.data ?? [])}
          loading={view === "invoiced" ? isLoading : awaiting.isLoading}
          awaiting={view === "awaiting"}
          toolbarExtra={
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Segmented
                  value={view}
                  onChange={setView}
                  options={[
                    { value: "invoiced", label: t("invoice.view.invoiced", "Invoiced") },
                    { value: "awaiting", label: t("invoice.view.awaiting", "Awaiting invoice") },
                  ]}
                />
                {view === "awaiting" && stuck > 0 && (
                  <Badge variant="error" size="sm">
                    {t("invoice.awaiting.stuck-count", "{{n}} stuck", { n: stuck })}
                  </Badge>
                )}
              </div>
              {view === "invoiced" && (
                <>
                  <label htmlFor="invoices-from" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                    {t("period.from", "From")}
                    <Input id="invoices-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="h-8 w-36" />
                  </label>
                  <label htmlFor="invoices-to" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                    {t("period.to", "To")}
                    <Input id="invoices-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} className="h-8 w-36" />
                  </label>
                </>
              )}
            </div>
          }
        />
      )}
    </>
  );
}
